import {
  PlaceholderPreviewBase,
  PlaceholderPreviewBaseProps,
} from "@/components/r3f/utils/placeholder-preview-base";
import { useImgPreviewSignedUrl } from "@/hooks/use-img-preview-signed-url";
import { useCached3DObjectIfExists } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import { GUID, isLoading } from "@faro-lotv/foundation";
import { isRevisionScanEntity } from "@faro-lotv/service-wires";
import { useEffect, useMemo, useState, type JSX } from "react";
import { useLazyLoadRevisionImg360 } from "../loading/use-load-revision-img360s";
import { selectRevisionEntity } from "../store/revision/revision-entity-selectors";
import { selectPointCloudStreamForScanEntity } from "../store/revision/revision-scan-selectors";

/**
 * Delay in milliseconds before the preview popover is hidden after the pointer
 * leaves both the anchor and the preview. This bridges the gap so the user can
 * move the mouse onto the preview to interact with the panorama viewer.
 */
export const PREVIEW_CLOSE_DELAY_MS = 250;

export const PREVIEW_OPEN_DELAY_MS = 300;

type ScanPreviewProps = Pick<
  PlaceholderPreviewBaseProps,
  | "anchorEl"
  | "placement"
  | "disablePortal"
  | "isVisible"
  | "popperModifiers"
  | "onPreviewHoverChange"
> & {
  /** The ID of the scan to show the preview for. */
  scanId: GUID;
  /** Whether the pointer is currently over the scan's hover target. */
  isHovering: boolean;
};

/**
 * @returns A preview popover for the scan, showing details such as name and an interactive
 * 360-degree panorama viewer. Shows nothing if the scan is not found.
 */
export function ScanPreview({
  scanId,
  isVisible = false,
  isHovering,
  anchorEl,
  onPreviewHoverChange,
  ...previewProps
}: ScanPreviewProps): JSX.Element | null {
  const [readyScanId, setReadyScanId] = useState<GUID>();
  useEffect(() => {
    if (!isVisible || (readyScanId && readyScanId !== scanId)) {
      setReadyScanId(undefined);
      return;
    }
    if (!isHovering || readyScanId === scanId) {
      return;
    }

    const timeout = setTimeout(
      () => setReadyScanId(scanId),
      PREVIEW_OPEN_DELAY_MS,
    );
    return () => clearTimeout(timeout);
  }, [scanId, isVisible, isHovering, readyScanId]);
  const isPreviewReady = isVisible && readyScanId === scanId;

  const entity = useAppSelector(selectRevisionEntity(scanId));
  const scanEntity = useMemo(
    () => (entity && isRevisionScanEntity(entity) ? entity : undefined),
    [entity],
  );

  const img360 = useLazyLoadRevisionImg360(scanEntity, isPreviewReady);

  const previewSignedUrl = useImgPreviewSignedUrl(
    isPreviewReady ? img360 : undefined,
  );

  const panoElement = !isPreviewReady || isLoading(img360) ? undefined : img360;
  // Load the pano object here (outside the popover) because the popover portal breaks React context
  const panoObject = useCached3DObjectIfExists(panoElement, "ScanPreview");
  const isDisabledScan = !useAppSelector(
    selectPointCloudStreamForScanEntity(scanEntity),
  );

  if (!isPreviewReady || !scanEntity || !anchorEl) {
    return null;
  }

  const disabledReason = isDisabledScan
    ? "This point cloud is currently unavailable and cannot be displayed"
    : undefined;

  return (
    <PlaceholderPreviewBase
      name={scanEntity.name}
      createdAt={scanEntity.createdAt}
      imageUri={previewSignedUrl}
      panoElement={panoElement}
      hasPanoramaMedia={!!panoObject}
      disabledReason={disabledReason}
      anchorEl={anchorEl}
      isVisible={isVisible}
      onPreviewHoverChange={onPreviewHoverChange}
      {...previewProps}
    />
  );
}
